import { api } from '@/lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 저장 대상 chat artifact 의 head id. dashboard.create 가 만든 currentVersion 의 spec 이 복사됨. */
  artifactId: string | null;
  /** 다이얼로그 열릴 때 기본 title (artifact.title). 사용자가 수정 가능. */
  defaultTitle: string;
  /** 저장 성공 시 호출. dashboardId 를 받아 view 페이지로 라우팅하거나 토스트. */
  onSaved?: (dashboardId: string) => void;
}

/**
 * 채팅 안에서 본 dashboard 를 영속 dashboards 로 저장하는 다이얼로그.
 * title 만 입력. visibility 는 기본 private — 공유 변경은 view 페이지에서.
 */
export function DashboardSaveDialog({
  open,
  onOpenChange,
  artifactId,
  defaultTitle,
  onSaved,
}: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle || '대시보드');
    setError(null);
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, defaultTitle]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!artifactId) throw new Error('저장 대상 artifact 가 없습니다.');
      return api.dashboardSave({ artifactId, title: title.trim() });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['dashboards', 'mine'] });
      onOpenChange(false);
      onSaved?.(res.id);
    },
    onError: (err) => setError(err instanceof Error ? err.message : '저장 실패'),
  });

  if (!open) return null;

  const submit = () => {
    if (!title.trim()) {
      setError('제목을 입력하세요.');
      return;
    }
    setError(null);
    saveMutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: ChatProjectFormDialog 와 동일 패턴 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="대시보드 저장"
        className="w-full max-w-md rounded-2xl border border-border bg-surface shadow-[var(--shadow-card)] overflow-hidden"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onOpenChange(false);
          } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <div className="px-5 pt-5 pb-1 text-sm font-semibold">대시보드 저장</div>
        <div className="px-5 pt-1 pb-3 text-xs text-foreground-muted">
          저장하면 왼쪽 사이드바의 "대시보드" 목록에 추가됩니다. 공유는 저장 후 view 페이지에서.
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="block">
            <span className="block text-xs text-foreground-muted mb-1">제목</span>
            <input
              ref={inputRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="대시보드 제목"
              className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm outline-none focus:border-accent"
            />
          </label>
          {error ? <div className="text-xs text-destructive">{error}</div> : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-8 px-3 rounded-md text-sm text-foreground-muted hover:bg-surface-muted"
          >
            취소
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saveMutation.isPending || !title.trim() || !artifactId}
            className="h-8 px-3 rounded-md text-sm bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-40"
          >
            {saveMutation.isPending ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
